import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Waveform, Lock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

import { API } from "@/lib/apiBase";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords don't match"); return; }
    if (!token) { toast.error("Invalid reset link"); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#0A0A0C" }}>
        <div className="text-center">
          <p className="text-white mb-4">Invalid reset link.</p>
          <Link to="/forgot-password" className="text-[#F59E0B] hover:text-white transition-colors">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#0A0A0C" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Waveform size={36} weight="bold" style={{ color: "#F59E0B" }} />
            <h1 className="text-3xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Daremusics</h1>
          </div>
          <p className="text-base" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>Set your new password</p>
        </div>

        <div className="bg-[#141417] border border-white/5 rounded-2xl p-6 md:p-8">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} weight="fill" style={{ color: "#10B981" }} />
              </div>
              <h3 className="text-xl text-white font-medium mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Password Reset!</h3>
              <p className="text-sm mb-6" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
                Your password has been updated. You can now login with your new password.
              </p>
              <Link to="/login" className="bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3 hover:bg-[#D97706] transition-all inline-block" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2" style={{ color: "#F59E0B" }}>New Password</label>
                <div className="bg-[#0A0A0C] border border-white/10 rounded-xl focus-within:border-[#F59E0B] transition-colors flex items-center gap-3 px-4">
                  <Lock size={18} style={{ color: "#A1A1AA" }} />
                  <input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="w-full bg-transparent py-3 text-white placeholder:text-gray-500 outline-none" style={{ fontFamily: "'Manrope', sans-serif" }} required />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2" style={{ color: "#F59E0B" }}>Confirm Password</label>
                <div className="bg-[#0A0A0C] border border-white/10 rounded-xl focus-within:border-[#F59E0B] transition-colors flex items-center gap-3 px-4">
                  <Lock size={18} style={{ color: "#A1A1AA" }} />
                  <input data-testid="reset-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" className="w-full bg-transparent py-3 text-white placeholder:text-gray-500 outline-none" style={{ fontFamily: "'Manrope', sans-serif" }} required />
                </div>
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={loading} className="bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3 hover:bg-[#D97706] transition-all disabled:opacity-50 w-full flex items-center justify-center gap-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : null}
                Reset Password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
